import React from 'react'
import { useTranslation } from 'react-i18next'
import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { Divider, FlowGrid, H2, List, ListItem, Paragraph, Separator } from '@fs/zion-ui'
import { ContentAdd } from '@fs/zion-icon'
import { useSlsStateContext } from '@fs/zion-sls'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import useRecords from '../../../../views/indexing/hooks/useRecords'
import DroppableHousehold from './components/DroppableHousehold'
import PersonListItem from './components/PersonListItem'
import Droppable from '../../../../views/indexing/components/ui/Droppable'
import PersonDragOverlay from './components/PersonDragOverlay'
import { useEditHouseholdDrag } from './hooks/useEditHouseholdDrag'
import { dashedDropTargetCss, dashedDropTargetActiveCss } from './utils/style.utils'

const sensorsActivationConstraint = {
  delay: 150,
  distance: 5,
}

const EditHouseholdContent = () => {
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const { thumbnails } = useSharedBrowserStateContext()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: sensorsActivationConstraint }),
    useSensor(TouchSensor, { activationConstraint: sensorsActivationConstraint })
  )

  const { allRecords: households, floatingPersons } = useRecords({
    images: zionSlsState?.images,
    currentImageArk: zionSlsState?.currentImageArk,
    thumbnails,
  })

  const { dragInfo, handleDragStart, handleDragEnd, handleDragCancel } = useEditHouseholdDrag({ households })

  const getNewRecordOverStyle = (isOver) => {
    if (isOver && dragInfo?.isDragging) {
      return dashedDropTargetActiveCss
    }
    return dashedDropTargetCss
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <Paragraph size="sm">
        {t('edit-household.instructions', 'Moving a name will remove its previous relationships.')}
      </Paragraph>

      <Divider marginY="xxs" />

      <FlowGrid columnCount={1} bordersX gutters="sm">
        {households?.map((record) => (
          <DroppableHousehold key={record?.id} record={record} dragInfo={dragInfo} />
        ))}
      </FlowGrid>

      {/* Names not in a household */}
      {floatingPersons?.length > 0 && (
        <>
          <Divider marginY="xxs" />
          <H2 size="H5">{t('edit-household.no-household.heading', 'Names not in a household')}</H2>
          <Separator size="xxs" />
          <List>
            {floatingPersons.map((person) => (
              <PersonListItem key={person?.id} person={person} dragInfo={dragInfo} />
            ))}
          </List>
        </>
      )}

      {/* New Household */}
      <Divider marginY="xxs" />
      <H2 size="H5">{t('edit-household.new-household.heading', 'New Household')}</H2>
      <Separator size="xs" />
      <Droppable id="new_record" setOverStyle={getNewRecordOverStyle}>
        <List>
          <ListItem
            startElement={<ListItem.Icon Icon={ContentAdd} />}
            primaryText={t('edit-household.new-household.description', 'Drag a name here to create a new household.')}
          />
        </List>
      </Droppable>
      <Separator size="xs" />

      <Divider marginY="xxs" />

      <PersonDragOverlay dragInfo={dragInfo} />
    </DndContext>
  )
}

export default EditHouseholdContent
